import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  BarChart3,
  Bot,
  FileText,
  LineChart,
  Loader2,
  MessageSquare,
  Paperclip,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  Target,
  X,
} from "lucide-react";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import IntegrationErrorBoundary from "@/components/common/ErrorBoundary";
import GlobalFilter from "@/components/common/GlobalFilter";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { useIntegrationContext } from "@/contexts/IntegrationContext";
import { useAccount } from "@/contexts/account.context";
import {
  aiAgentsService,
  AgentInsightResponse,
  AgentSource,
  AgentChatMessage,
  AgentHistoryMessage,
} from "@/services/ai-agents.service";
import { differenceInCalendarDays, subDays } from "date-fns";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  StandardizedTable,
  TableColumn,
  TableRow,
} from "@/components/ui/standardized-table";
import { Skeleton } from "@/components/ui/skeleton";

type MarkdownNode = {
  children?: MarkdownNode[];
  tagName?: string;
  value?: string;
};

interface AgentSourceConfig {
  id: AgentSource;
  label: string;
  description: string;
  integrationKey: "google" | "meta";
  gradient: string;
  accent: string;
  icon: LucideIcon;
}

interface ChatEntry {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
  isInsight?: boolean;
  attachments?: Array<{ name: string; size: string }>;
}

interface AgentState {
  status: "idle" | "loading" | "ready" | "error";
  data?: AgentInsightResponse;
  error?: string;
}

const AGENT_SOURCES: AgentSourceConfig[] = [
  {
    id: "google-ads",
    label: "Google Ads",
    description: "Insights acionáveis sobre campanhas de mídia paga",
    integrationKey: "google",
    gradient: "",
    accent: "text-muted-foreground",
    icon: Target,
  },
  {
    id: "meta-ads",
    label: "Meta Ads",
    description: "Diagnóstico completo de campanhas no ecossistema Meta",
    integrationKey: "meta",
    gradient: "",
    accent: "text-muted-foreground",
    icon: BarChart3,
  },
  {
    id: "google-analytics",
    label: "Google Analytics",
    description: "Narrativas sobre comportamento, funil e receita",
    integrationKey: "google",
    gradient: "",
    accent: "text-muted-foreground",
    icon: LineChart,
  },
];

const formatDate = (date: string | undefined) => {
  if (!date) return "N/D";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
  }).format(new Date(date));
};

const formatTimestamp = (date: string | undefined) => {
  if (!date) return "";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(date));
};

const formatFileSize = (bytes: number) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

const createMessageId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substring(2, 10);
};

const MAX_ATTACHMENTS = 3;
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

const getNodeText = (node: MarkdownNode | null | undefined): string => {
  if (!node) return "";
  if (typeof node.value === "string") return node.value;
  if (Array.isArray(node.children))
    return node.children.map(getNodeText).join("");
  return "";
};

const parseMarkdownTableNode = (
  node: MarkdownNode | undefined,
): { columns: TableColumn[]; data: TableRow[] } => {
  try {
    const children = Array.isArray(node?.children) ? node.children : [];
    const thead = children.find((c) => c?.tagName === "thead");
    const tbody = children.find((c) => c?.tagName === "tbody");

    const headerTr = thead?.children?.find((c) => c?.tagName === "tr");
    const headerCells = Array.isArray(headerTr?.children)
      ? headerTr.children
      : [];
    const headers = headerCells
      .filter((c) => c?.tagName === "th")
      .map((th) => getNodeText(th).trim());

    const bodyTrs = Array.isArray(tbody?.children)
      ? tbody.children.filter((c) => c?.tagName === "tr")
      : [];
    const rows = bodyTrs.map((tr) => {
      const tds = Array.isArray(tr?.children)
        ? tr.children.filter((c) => c?.tagName === "td")
        : [];
      return tds.map((td) => getNodeText(td).trim());
    });

    const maxCols = Math.max(headers.length, ...rows.map((r) => r.length), 0);
    if (maxCols === 0) return { columns: [], data: [] };

    const columns: TableColumn[] = Array.from({ length: maxCols }).map(
      (_, idx) => ({
        key: `c${idx}`,
        header: headers[idx] || `Coluna ${idx + 1}`,
        type: "text",
        align: "left",
        width: idx === 0 ? "28ch" : "18ch",
        sortable: false,
        render: (value) => (
          <span className="block truncate" title={String(value ?? "")}>
            {String(value ?? "")}
          </span>
        ),
      }),
    );

    const data = rows.map((cells) => {
      const row: TableRow = {};
      for (let i = 0; i < maxCols; i += 1) {
        row[`c${i}`] = cells[i] ?? "";
      }
      return row;
    });

    return { columns, data };
  } catch {
    return { columns: [], data: [] };
  }
};

const MARKDOWN_COMPONENTS: Components = {
  h1: ({ node, ...props }) => (
    <h3 className="mt-0 text-lg font-semibold text-foreground" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h4 className="mt-0 text-base font-semibold text-foreground" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h5 className="mt-0 text-base font-semibold text-foreground" {...props} />
  ),
  p: ({ node, ...props }) => (
    <p className="my-2 break-words leading-relaxed" {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="my-2 list-disc space-y-1 pl-5" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="my-2 list-decimal space-y-1 pl-5" {...props} />
  ),
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="my-3 border-l-2 border-border pl-3 italic text-muted-foreground"
      {...props}
    />
  ),
  hr: () => <Separator className="my-4" />,
  table: ({ node, ...props }) => {
    const parsed = parseMarkdownTableNode(node);
    if (!parsed.columns.length) {
      return (
        <div className="my-3 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Não foi possível renderizar esta tabela no padrão do sistema.
        </div>
      );
    }
    return (
      <div className="my-3">
        <StandardizedTable
          title="Tabela"
          columns={parsed.columns}
          data={parsed.data}
          mensagemVazia="A tabela da resposta veio sem linhas."
          className="shadow-none"
        />
      </div>
    );
  },
  th: ({ node, ...props }) => (
    <th
      className="border-b border-border bg-muted px-3 py-2 text-[0.7rem] font-semibold uppercase tracking-wide text-foreground"
      {...props}
    />
  ),
  td: ({ node, ...props }) => (
    <td className="border-b border-border px-3 py-2 align-top" {...props} />
  ),
  code({ node, className, children, ...props }) {
    const languageMatch = /language-(\w+)/.exec(className || "");
    if (!languageMatch) {
      return (
        <code
          className="rounded bg-muted px-1 py-0.5 text-[0.75rem] font-mono text-foreground"
          {...props}
        >
          {children}
        </code>
      );
    }

    return (
      <pre className="my-3 overflow-x-auto rounded-lg bg-muted p-4 text-xs leading-relaxed">
        <code className="font-mono text-foreground" {...props}>
          {children}
        </code>
      </pre>
    );
  },
};

const MarkdownMessage: React.FC<{
  content: string;
  tone: "user" | "assistant";
}> = ({ content, tone }) => (
  <div
    className={cn(
      "prose prose-sm max-w-none leading-relaxed dark:prose-invert",
      tone === "user"
        ? "prose-headings:text-primary-foreground prose-p:text-primary-foreground prose-strong:text-primary-foreground"
        : "prose-headings:text-foreground prose-p:text-muted-foreground prose-strong:text-foreground",
    )}
  >
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
      {content}
    </ReactMarkdown>
  </div>
);

const mapHistoryMessagesToEntries = (
  messages: AgentHistoryMessage[],
): ChatEntry[] => {
  let insightAssigned = false;
  return messages.map((message) => {
    const isAssistant = message.role === "assistant";
    const isInsight = isAssistant && !insightAssigned;
    if (isInsight) {
      insightAssigned = true;
    }

    return {
      id: createMessageId(),
      role: message.role,
      content: message.content,
      createdAt: message.createdAt,
      isInsight,
    };
  });
};

const AIInsightsPage: React.FC = () => {
  const { toast } = useToast();
  const { filter } = useGlobalFilter();
  const { state: integrationState } = useIntegrationContext();
  const { selectedAccount } = useAccount();

  const accountId =
    selectedAccount?.id ||
    localStorage.getItem("active_subaccount_id") ||
    undefined;
  const currentRange = filter.dateRange;
  const filterKey = `${currentRange.startDate.toISOString()}-${currentRange.endDate.toISOString()}`;

  const [agentState, setAgentState] = useState<Record<AgentSource, AgentState>>(
    {
      "google-ads": { status: "idle" },
      "meta-ads": { status: "idle" },
      "google-analytics": { status: "idle" },
    },
  );
  const [selectedSource, setSelectedSource] =
    useState<AgentSource>("google-ads");
  const [chatHistory, setChatHistory] = useState<
    Record<AgentSource, ChatEntry[]>
  >({
    "google-ads": [],
    "meta-ads": [],
    "google-analytics": [],
  });
  const [userMessage, setUserMessage] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedConfig = useMemo(
    () =>
      AGENT_SOURCES.find((config) => config.id === selectedSource) ??
      AGENT_SOURCES[0],
    [selectedSource],
  );

  const connectionStatus = (key: "google" | "meta") => {
    const mapKey = key === "google" ? "google-main" : "meta-main";
    return integrationState.integrations.get(mapKey)?.status ?? "disconnected";
  };

  const toISODate = (value: Date) => value.toISOString().split("T")[0];

  const synchronizeConversationHistory = useCallback(
    async (source: AgentSource, conversationId: string) => {
      try {
        const result = await aiAgentsService.fetchHistory(
          source,
          conversationId,
          1,
          100,
        );
        const normalizedHistory = mapHistoryMessagesToEntries(result.messages);
        if (!normalizedHistory.length) {
          return false;
        }

        setChatHistory((prev) => ({
          ...prev,
          [source]: normalizedHistory,
        }));
        return true;
      } catch (error) {
        console.error("Falha ao carregar histórico do agente", error);
        return false;
      }
    },
    [setChatHistory],
  );

  const loadAgentInsight = async (source: AgentSource, silent = false) => {
    const start = currentRange.startDate;
    const end = currentRange.endDate;
    const windowLength = differenceInCalendarDays(end, start) + 1;
    const previousEndDate = subDays(start, 1);
    const previousStartDate = subDays(
      previousEndDate,
      Math.max(windowLength - 1, 0),
    );
    const params = {
      startDate: toISODate(start),
      endDate: toISODate(end),
      previousStartDate: toISODate(previousStartDate),
      previousEndDate: toISODate(previousEndDate),
      accountId,
      signal: abortControllerRef.current?.signal,
    };

    setAgentState((prev) => ({
      ...prev,
      [source]: { status: "loading", data: prev[source]?.data },
    }));

    try {
      const response = await aiAgentsService.fetchInsight(source, params);
      setAgentState((prev) => ({
        ...prev,
        [source]: { status: "ready", data: response },
      }));

      const baseInsight: ChatEntry = {
        id: createMessageId(),
        role: "assistant",
        content: response.message || "Nenhum insight disponível no momento.",
        createdAt: response.startedAt || new Date().toISOString(),
        isInsight: true,
      };

      setChatHistory((prev) => ({
        ...prev,
        [source]: [baseInsight],
      }));

      if (response.conversationId) {
        await synchronizeConversationHistory(source, response.conversationId);
      }

      if (!silent) {
        toast({
          title: `Insight atualizado - ${AGENT_SOURCES.find((cfg) => cfg.id === source)?.label ?? ""}`,
          description:
            "O agente recalculou o insight usando os filtros atuais.",
        });
      }
    } catch (error) {
      // Ignorar erros de cancelamento (usuário saiu da página)
      if (
        error instanceof Error &&
        (error.name === "AbortError" || error.name === "CanceledError")
      ) {
        return;
      }

      const message =
        error instanceof Error
          ? error.message
          : "Não foi possível carregar este agente no momento.";
      setAgentState((prev) => ({
        ...prev,
        [source]: { status: "error", error: message },
      }));

      if (!silent) {
        toast({
          title: "Erro ao buscar insight",
          description: message,
          variant: "destructive",
        });
      }
    }
  };

  // AbortController para cancelar requisições quando o componente é desmontado
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Cancelar qualquer requisição anterior
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Criar novo AbortController para este ciclo
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const run = async () => {
      // Carregar insights sequencialmente para reduzir carga no servidor
      for (const config of AGENT_SOURCES) {
        if (controller.signal.aborted) return;
        try {
          await loadAgentInsight(config.id, true);
        } catch (error) {
          // Ignorar erros de cancelamento
          if (error instanceof Error && error.name === "AbortError") {
            return;
          }
          console.error(`Erro ao carregar insight ${config.id}:`, error);
        }
      }
    };

    run();

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, accountId]);

  useEffect(() => {
    if (agentState[selectedSource]?.status === "ready") return;
    const firstReady = AGENT_SOURCES.find(
      (source) => agentState[source.id]?.status === "ready",
    );
    if (firstReady) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedSource(firstReady.id);
    }
  }, [agentState, selectedSource]);

  const currentInsight = agentState[selectedSource]?.data;
  const history = chatHistory[selectedSource] ?? [];

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (!files.length) return;

    const merged = [...pendingFiles];

    for (const file of files) {
      if (merged.length >= MAX_ATTACHMENTS) {
        toast({
          title: "Limite de anexos atingido",
          description: `Envie no máximo ${MAX_ATTACHMENTS} arquivos por mensagem.`,
        });
        break;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: "Arquivo muito grande",
          description: `${file.name} excede ${formatFileSize(MAX_FILE_SIZE)}.`,
          variant: "destructive",
        });
        continue;
      }

      merged.push(file);
    }

    setPendingFiles(merged);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSendMessage = async () => {
    if (!currentInsight) {
      toast({
        title: "Insight indisponível",
        description: "Atualize o agente antes de iniciar uma conversa.",
      });
      return;
    }

    if (!userMessage.trim() && pendingFiles.length === 0) {
      return;
    }

    const userEntry: ChatEntry = {
      id: createMessageId(),
      role: "user",
      content: userMessage.trim() || "(anexos enviados)",
      createdAt: new Date().toISOString(),
      attachments: pendingFiles.map((file) => ({
        name: file.name,
        size: formatFileSize(file.size),
      })),
    };

    setChatHistory((prev) => ({
      ...prev,
      [selectedSource]: [...(prev[selectedSource] ?? []), userEntry],
    }));

    setUserMessage("");
    setPendingFiles([]);
    setIsSending(true);

    try {
      const response = await aiAgentsService.sendMessage({
        source: selectedSource,
        insight: currentInsight.message ?? "",
        userMessage: userEntry.content,
        history: history.map<AgentChatMessage>((entry) => ({
          role: entry.role,
          content: entry.content,
        })),
        filters: currentInsight.filters,
        conversationId: currentInsight.conversationId,
        attachments: pendingFiles,
      });

      const assistantEntry: ChatEntry = {
        id: createMessageId(),
        role: "assistant",
        content: response.response,
        createdAt: new Date().toISOString(),
      };

      setChatHistory((prev) => ({
        ...prev,
        [selectedSource]: [...(prev[selectedSource] ?? []), assistantEntry],
      }));

      const updatedConversationId =
        response.conversationId ?? currentInsight.conversationId;
      if (updatedConversationId) {
        await synchronizeConversationHistory(
          selectedSource,
          updatedConversationId,
        );
      }
    } catch (error) {
      setChatHistory((prev) => ({
        ...prev,
        [selectedSource]: (prev[selectedSource] ?? []).filter(
          (msg) => msg.id !== userEntry.id,
        ),
      }));

      const description =
        error instanceof Error
          ? error.message
          : "Não foi possível conversar com o agente agora. Tente novamente.";
      toast({
        title: "Falha ao enviar mensagem",
        description,
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const renderCardBody = (state: AgentState, sourceId: AgentSource) => {
    if (state.status === "loading") {
      return (
        <div className="space-y-3">
          <div className="h-4 w-48 bg-muted rounded animate-pulse" />
          <div className="h-[88px] w-full bg-muted/50 rounded-lg animate-pulse" />
          <div className="flex gap-2">
            <div className="h-10 flex-1 bg-muted/50 rounded animate-pulse" />
            <div className="h-10 flex-1 bg-muted/50 rounded animate-pulse" />
          </div>
        </div>
      );
    }

    if (state.status === "error") {
      return (
        <div className="space-y-3">
          <p className="text-sm text-destructive">{state.error}</p>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => loadAgentInsight(sourceId)}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Tentar novamente
          </Button>
        </div>
      );
    }

    if (!state.data) {
      return (
        <p className="text-sm text-muted-foreground">
          Conecte a integração e gere seu primeiro insight.
        </p>
      );
    }

    return (
      <div className="space-y-4">
        <p className="text-sm text-foreground/90 line-clamp-4 whitespace-pre-line">
          {state.data.message}
        </p>
        <div className="text-xs text-muted-foreground flex flex-wrap gap-4">
          <span>
            Período: {formatDate(state.data.filters.startDate)} —{" "}
            {formatDate(state.data.filters.endDate)}
          </span>
          <span>
            Última análise:{" "}
            {state.data.startedAt
              ? formatTimestamp(state.data.startedAt)
              : "N/D"}
          </span>
        </div>
      </div>
    );
  };

  const renderChatBody = () => {
    if (agentState[selectedSource]?.status === "loading") {
      return (
        <div className="flex-1 p-4 space-y-4">
          <div className="flex items-start gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-24 w-full rounded-lg" />
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-16 w-3/4 rounded-lg" />
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-32 w-full rounded-lg" />
            </div>
          </div>
        </div>
      );
    }

    if (!currentInsight) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center text-center space-y-3 text-muted-foreground">
          <Sparkles className="h-8 w-8 text-primary" />
          <p>
            Nenhum insight disponível ainda para {selectedConfig.label}. Clique
            em &quot;Atualizar&quot; para gerar uma análise com os filtros
            atuais.
          </p>
          <Button
            variant="secondary"
            onClick={() => loadAgentInsight(selectedSource)}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar agente
          </Button>
        </div>
      );
    }

    return (
      <>
        <ScrollArea className="flex-1 pr-4">
          <div className="flex flex-col gap-4">
            {history.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "flex",
                  message.role === "user" ? "justify-end" : "justify-start",
                )}
              >
                <div
                  className={cn(
                    "max-w-[80%] rounded-2xl px-4 py-3 shadow",
                    message.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  <div className="flex items-center gap-2 text-xs opacity-70 mb-1">
                    {message.role === "user" ? (
                      <ShieldCheck className="h-4 w-4" />
                    ) : (
                      <Bot className="h-4 w-4" />
                    )}
                    <span>{formatTimestamp(message.createdAt)}</span>
                    {message.isInsight && (
                      <Badge variant="outline">Insight</Badge>
                    )}
                  </div>
                  <MarkdownMessage
                    content={message.content}
                    tone={message.role}
                  />
                  {message.attachments && (
                    <div className="mt-3 space-y-1">
                      {message.attachments.map((file) => (
                        <div
                          key={`${message.id}-${file.name}`}
                          className="flex items-center gap-2 text-xs"
                        >
                          <Paperclip className="h-3 w-3" />
                          <span>{file.name}</span>
                          <span className="opacity-70">{file.size}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isSending && (
              <div className="flex justify-start text-muted-foreground text-sm gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Agente digitando...</span>
              </div>
            )}
          </div>
        </ScrollArea>

        <Separator className="my-4" />

        <div className="space-y-3">
          {pendingFiles.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {pendingFiles.map((file, index) => (
                <Badge
                  key={`${file.name}-${index}`}
                  variant="secondary"
                  className="flex items-center gap-2"
                >
                  <FileText className="h-3 w-3" />
                  <span className="text-xs">{file.name}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 p-0"
                    onClick={() => removeFile(index)}
                    title="Remover"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            </div>
          )}
          <div className="flex flex-col gap-3 lg:flex-row">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSending}
              >
                <Paperclip className="h-4 w-4" />
                Anexar arquivos
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
            <div className="flex flex-1 gap-2">
              <Textarea
                value={userMessage}
                onChange={(event) => setUserMessage(event.target.value)}
                placeholder="Converse com o agente sobre este insight..."
                className="flex-1"
                rows={2}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <Button
                onClick={handleSendMessage}
                disabled={
                  isSending ||
                  (!userMessage.trim() && pendingFiles.length === 0)
                }
                className="self-end"
              >
                {isSending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <IntegrationErrorBoundary>
      <div className="p-6 space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Insights IA
            </h1>
            <p className="text-sm text-muted-foreground max-w-xl mt-1">
              Análises especializadas por fonte de dados com agentes de IA para
              contextualizar resultados.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 px-3 py-2 rounded-lg">
            <ShieldCheck className="h-4 w-4 text-green-500" />
            <span>Integrações conectadas</span>
          </div>
        </div>

        <GlobalFilter />

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {AGENT_SOURCES.map((config) => {
            const state = agentState[config.id];
            const isSelected = config.id === selectedSource;
            const status = connectionStatus(config.integrationKey);

            return (
              <Card
                key={config.id}
                className={cn(
                  "relative overflow-hidden transition-all duration-200",
                  isSelected
                    ? "border-primary shadow-sm"
                    : "border-border hover:border-muted-foreground/30",
                )}
              >
                <div className="relative p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">
                        Agente
                      </p>
                      <h2 className="text-xl font-semibold text-foreground">
                        {config.label}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {config.description}
                      </p>
                    </div>
                    <div
                      className={cn(
                        "flex items-center justify-center h-12 w-12 rounded-lg",
                        isSelected ? "bg-primary/10" : "bg-muted",
                      )}
                    >
                      <config.icon
                        className={cn(
                          "h-6 w-6",
                          isSelected ? "text-primary" : "text-muted-foreground",
                        )}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge
                      variant="secondary"
                      className={cn(
                        "text-xs font-normal",
                        status === "connected"
                          ? "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20"
                          : "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20",
                      )}
                    >
                      {status === "connected" ? "Conectado" : "Pendente"}
                    </Badge>
                    {isSelected && (
                      <Badge variant="default" className="text-xs font-normal">
                        Selecionado
                      </Badge>
                    )}
                  </div>

                  {renderCardBody(state, config.id)}

                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadAgentInsight(config.id)}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Atualizar
                    </Button>
                    <Button
                      size="sm"
                      variant={isSelected ? "secondary" : "default"}
                      onClick={() => setSelectedSource(config.id)}
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Conversar
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        <Card className="border">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Bot className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg font-medium">
                    {selectedConfig.label}
                  </CardTitle>
                </div>
                <p className="text-sm text-muted-foreground">
                  Converse com o assistente sobre os dados e receba
                  recomendações.
                </p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadAgentInsight(selectedSource)}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Atualizar
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reprocessa o insight com os filtros atuais.</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-2">
              <span>
                Período: {formatDate(currentInsight?.filters.startDate)} —{" "}
                {formatDate(currentInsight?.filters.endDate)}
              </span>
              <span>
                Comparação:{" "}
                {formatDate(currentInsight?.filters.previousStartDate)} —{" "}
                {formatDate(currentInsight?.filters.previousEndDate)}
              </span>
            </div>
          </CardHeader>
          <CardContent className="flex h-[500px] flex-col pt-0">
            {renderChatBody()}
          </CardContent>
        </Card>
      </div>
    </IntegrationErrorBoundary>
  );
};

export default AIInsightsPage;
